<script setup lang="ts">
/**
 * DS Modal `Footer` (324:4825): an optional quiet second button and the main
 * one, each half the width. The main one is `primary`, or the disabled chip
 * fill while it cannot be pressed. Mobile buttons are 48 tall.
 */
import { FairIcon } from '@/ui/primitives/FairIcon'
import type { FairIconName } from '@/ui/tokens/icons.data'

interface FairDialogFooterProps {
  cancelLabel?: string
  confirmLabel?: string
  showCancel?: boolean
  confirmDisabled?: boolean
  /** Saving: the main button keeps its size and shows a spinner. */
  confirmLoading?: boolean
  mobile?: boolean
  cancelLeadingIcon?: FairIconName
  cancelTrailingIcon?: FairIconName
  confirmLeadingIcon?: FairIconName
  confirmTrailingIcon?: FairIconName
}

withDefaults(defineProps<FairDialogFooterProps>(), {
  cancelLabel: 'Cancel',
  confirmLabel: 'Confirm',
  showCancel: true,
  confirmDisabled: false,
  confirmLoading: false,
  mobile: false,
  cancelLeadingIcon: undefined,
  cancelTrailingIcon: undefined,
  confirmLeadingIcon: undefined,
  confirmTrailingIcon: undefined,
})

const emit = defineEmits<{
  cancel: [event: MouseEvent]
  confirm: [event: MouseEvent]
}>()
</script>

<template>
  <footer
    class="fair-dialog-footer"
    :class="{ 'fair-dialog-footer--mobile': mobile }"
  >
    <slot>
      <button
        v-if="showCancel"
        type="button"
        class="fair-dialog-footer__button fair-dialog-footer__button--quiet"
        @click="emit('cancel', $event)"
      >
        <FairIcon
          v-if="cancelLeadingIcon"
          :name="cancelLeadingIcon"
          :size="16"
        />
        {{ cancelLabel }}
        <FairIcon
          v-if="cancelTrailingIcon"
          :name="cancelTrailingIcon"
          :size="16"
        />
      </button>
      <button
        type="button"
        class="fair-dialog-footer__button fair-dialog-footer__button--primary"
        :disabled="confirmDisabled || confirmLoading"
        :aria-busy="confirmLoading"
        :class="{ 'fair-dialog-footer__button--loading': confirmLoading }"
        @click="emit('confirm', $event)"
      >
        <span
          v-if="confirmLoading"
          class="fair-dialog-footer__spinner"
          aria-hidden="true"
        />
        <template v-else>
          <FairIcon
            v-if="confirmLeadingIcon"
            :name="confirmLeadingIcon"
            :size="16"
          />
          {{ confirmLabel }}
          <FairIcon
            v-if="confirmTrailingIcon"
            :name="confirmTrailingIcon"
            :size="16"
          />
        </template>
      </button>
    </slot>
  </footer>
</template>
